import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CountUp from 'react-countup';
import axiosInstance from '../../../../config/axiosInstance';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import MapSection from './MapSection';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ChartWithDownload from '../../../../components/ChartWithDownload/ChartWithDownload';
const API_PREFIX = process.env.REACT_APP_API_BASE_URL;

/**
 * AnalyticsManager component
 *
 * Props:
 *   alias (string): Station alias to use in API calls (required for analytics endpoints)
 */
const AnalyticsManager = () => {
    const alias = useSelector((state) => state.auth.user.attributes.alias) || '';

    // Filter state
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [interval, setInterval] = useState('month');
    const [platform, setPlatform] = useState('all');
    const [listener, setListener] = useState('all');
    const [filters, setFilters] = useState({});

    // Analytics/statistics state
    const [stats, setStats] = useState({
        ios: { weekly: 0, recordsTill: '', all_time: 0 },
        android: { weekly: 0, recordsTill: '', all_time: 0 },
    });
    const [statsLoading, setStatsLoading] = useState(true);
    const [totalActivity, setTotalActivity] = useState(0);
    const [sessionStarts, setSessionStarts] = useState(0);
    const [chatMessage, setChatMessage] = useState(0);
    const [pollsAnswer, setPollsAnswer] = useState(0);
    const [contestSubmission, setContestSubmission] = useState(0);
    const [wheelIconClicks, setWheelIconClicks] = useState(0);
    const [totalSessions, setTotalSessions] = useState(0);
    const [uniqueListeners, setUniqueListeners] = useState(0);
    const [regListeners, setRegListeners] = useState(0);
    const [averageListeningTime, setAverageListeningTime] = useState(0);

    // Loading state
    const [loading, setLoading] = useState(false);

    // --- Add state for each chart's data ---
    const [sessionData, setSessionData] = useState([]);
    const [listenerData, setListenerData] = useState([]);
    const [listenerDayData, setListenerDayData] = useState([]);
    const [listeningSessionDuration, setListeningSessionDuration] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [genderBreakdown, setGenderBreakdown] = useState([]);
    const [ageBreakdown, setAgeBreakdown] = useState([]);
    const [regionData, setRegionData] = useState([]);
    const [regionDataLoading, setRegionDataLoading] = useState(false);
    const [adsForStationData, setAdsForStationData] = useState([]);

    const intervalItems = [
        { value: 'today', label: 'Last 24 hours' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'week', label: 'Last Week' },
        { value: 'month', label: 'Last Month' },
        { value: 'custom', label: 'Custom Period' },
    ];
    const platformItems = [
        { value: 'all', label: 'All' },
        { value: 'Android', label: 'Android' },
        { value: 'iOS', label: 'iOS' },
    ];
    const listenerItems = [
        { value: 'all', label: 'All' },
        { value: 'register', label: 'Logged In' },
        { value: 'anonymous', label: 'Anonymous' },
    ];

    // Replace the <style> tag and className usage with a styles object
    const styles = {
        PageContainer: {
            padding: '0 16px 24px 16px',
        },
        FilterContainer: {
            display: 'flex',
            padding: '20px',
            margin: '10px',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
        },
        Button: {
            flexDirection: 'column',
            justifyContent: 'space-around',
            color: 'white',
            padding: '8px 16px',
            background: 'rgb(74, 144, 226)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
        },
        Select: {
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            minWidth: '140px',
        },
        Date: {
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            minWidth: '140px',
        },
        CardRow: {
            display: 'flex',
            gap: '16px',
            margin: '20px 0',
        },
        Card: {
            flex: 1,
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '24px',
            minWidth: '260px',
        },
        CardTitle: {
            fontSize: '20px',
            fontWeight: 500,
            marginBottom: '12px',
        },
        CardSubtitle: {
            fontSize: '14px',
            color: '#888',
            marginBottom: '16px',
        },
        StatRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
        },
        StatLabel: {
            fontSize: '14px',
            color: '#555',
        },
        StatValue: {
            fontSize: '28px',
            fontWeight: 600,
            color: '#3A3E52',
        },
        MainCard: {
            margin: '20px 0',
        },
        MainCardInner: {
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '24px',
        },
        FlexRow: {
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            textAlign: 'center',
        },
        ChartCard: {
            margin: '20px 0',
        },
        ChartCardInner: {
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '24px',
            width: '100%',
            minWidth: 0,
        },
        ChartTitle: {
            fontSize: '20px',
            fontWeight: 500,
            marginBottom: '20px',
        },
        ChartSubtitle: {
            fontSize: '14px',
            color: '#888',
            marginBottom: '20px',
        },
        FlexGap: {
            display: 'flex',
            gap: '16px',
            margin: '20px 0',
        },
        PieCard: {
            flex: '0 0 28%',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '24px',
            minWidth: 0,
            minHeight: 420,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
         
        },
        PieLegend: {
            color: '#888',
            fontSize: 12,
            marginTop: 10,
        },
        PieLegendDot: {
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            marginRight: 6,
        },
        LineCard: {
            flex: '0 0 60%',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '24px',
            minWidth: 0,
        },
        AdSectionCard: {
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: 20,
            marginBottom: 24,
        },
        AdSectionHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
            flexWrap: 'wrap',
        },
        AdSectionTitle: {
            fontSize: 20,
            fontWeight: 600,
            color: '#2e3448',
            margin: 0,
        },
        AdChartsRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
            marginBottom: 16,
        },
        AdTableWrap: {
            overflowX: 'auto',
            border: '1px solid #e9edf5',
            borderRadius: 8,
        },
        AdTable: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: 640,
        },
        AdTableHeadCell: {
            padding: '10px 12px',
            fontSize: 13,
            color: '#5b6476',
            background: '#f8faff',
            borderBottom: '1px solid #e9edf5',
            textAlign: 'left',
            fontWeight: 600,
            whiteSpace: 'nowrap',
        },
        AdTableCell: {
            padding: '10px 12px',
            fontSize: 13,
            color: '#2f3442',
            borderBottom: '1px solid #f0f2f7',
            whiteSpace: 'nowrap',
        },
        AdTableTotalRow: {
            fontWeight: 700,
            background: '#fafcff',
        },
        MapSectionWrap: {
            margin: '20px 0 24px 0',
            paddingLeft: 4,
            paddingRight: 4,
        },
        MultiSelect: {
            position: 'relative',
            minWidth: 320,
        },
        MultiSelectSummary: {
            listStyle: 'none',
            border: '1px solid #ccd5e3',
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: 13,
            color: '#2f3442',
            background: '#fff',
            cursor: 'pointer',
        },
        MultiSelectMenu: {
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            width: '100%',
            maxHeight: 240,
            overflowY: 'auto',
            border: '1px solid #ccd5e3',
            borderRadius: 8,
            background: '#fff',
            boxShadow: '0 8px 24px rgba(17,24,39,0.12)',
            zIndex: 20,
            padding: 8,
        },
        MultiSelectOption: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 4px',
            fontSize: 13,
            color: '#2f3442',
        },
    };

    useEffect(() => {
        const newFilters = { startDate, endDate, interval, platform, listener };
        setFilters(newFilters);
        fetchAnalyticsData();
    }, []);

    // Format date as YYYY-MM-DD (avoid timezone issues)
    const formatDate = (date) => {
        // If date is already in YYYY-MM-DD format, return as is
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }
        
        // For other date formats, parse without timezone conversion
        const d = new Date(date);
        // Use UTC methods to avoid timezone conversion
        let month = `${d.getUTCMonth() + 1}`;
        let day = `${d.getUTCDate()}`;
        const year = d.getUTCFullYear();
        if (month.length < 2) month = `0${month}`;
        if (day.length < 2) day = `0${day}`;
        return [year, month, day].join('-');
    };
    const simpleFormatDate = (dateString) => {
        const [month, day, year] = dateString.split("/");
        return `${year}-${month}-${day}`;
      }

    // Convert seconds to HH:MM:SS
    const formatSecondsToHHMMSS = (d) => {
        let h = Math.floor(d / 3600);
        let m = Math.floor((d % 3600) / 60);
        let s = Math.floor(d % 3600 % 60);
        h = h < 10 ? '0' + h : h;
        m = m < 10 ? '0' + m : m;
        s = s < 10 ? '0' + s : s;
        return `${h}:${m}:${s}`;
    };

    // Build query params
    const buildParams = () => {
        if (!alias) {
            toast.error("Error occurred while fetching analytics data, Please login again");
            return;
        } // Don't build params if alias is missing
        if (interval !== 'custom') {
            return `dateRange=${interval}&stationId=${alias}&platform=${platform}&listener=${listener}`;
        } else {
            return `dateRange=${interval}&startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}&stationId=${alias}&platform=${platform}&listener=${listener}`;
        }
    };

    // Separate API call functions
    const fetchActivityStats = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/activity-stats-for-station?${param}`);
        return res.data.data;
    };

    const fetchSessionsStats = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/all-sessions?${param}`);
        return res.data.data;
    };

    // --- API call functions for each chart ---
    const fetchListenersPerDay = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/listeners-per-day-for-a-station?${param}`);
        const list = (res.data.data[0] || []).map((item) => ({
            ...item,
            date: formatDate(item.date),
        }));
        return list;
    };

    const fetchSessionsPerDay = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/sessions-per-day-for-a-station?${param}`);
        const list = (res.data.data[0] || []).map((item) => ({
            ...item,
            date: formatDate(item.date),
        }));
        return list.length === 0 ? [{ date: formatDate(new Date()), sessionCount: 0 }] : list;
    };

    const fetchListeningHoursPerDay = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/listening-hours-per-day-for-a-station?${param}`);
        const list = (res.data.data[0] || []).map((item) => ({
            ...item,
            date: formatDate(item.date),
        }));
        return list.length === 0 ? [{ date: formatDate(new Date()), listeningHoursPerDay: 0 }] : list;
    };

    const fetchListeningSessionDuration = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/listening-session-duration-distribution-for-station?${param}`);
        return res.data.data.map(({ name, vale }) => ({ name, Total: vale }));
    };

    const fetchChatMessagesPerDay = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/chat-messages-per-day-for-station?${param}`);
        const arr = res.data.data[0] || [];
        return arr.length === 0 ? [{ date: formatDate(new Date()), chatMessagesCount: 0 }] : arr;
    };

    const fetchGenderBreakdown = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/gender-breakdown-for-station?${param}`);
        // Map 'vale' to 'value' for recharts PieChart
        return (res.data.data || []).map(item => ({ name: item.name, value: item.vale }));
    };

    const fetchAgeBreakdown = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/age-breakdown-by-years-for-station?${param}`);
        return res.data.data.map(({ name, vale }) => ({ name, Total: vale }));
    };

    const fetchAdsForStation = async (param) => {
        const res = await axiosInstance.get(`${API_PREFIX}/analytics/ads-for-a-station?${param}`);
        return (res?.data?.data?.[0] || []);
    };

    const fetchRegionData = async (param) => {
        setRegionDataLoading(true);
        let existingCoordinates = [];
        const MAX_RETRIES = 2;
        const RETRY_DELAY = 1000;
        const COORDINATES_TIMEOUT_MS = 8000;

        try {
            for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                try {
                    console.log(`Loading coordinates.json (attempt ${attempt + 1}/${MAX_RETRIES + 1})`);
                    const coordinatesResponse = await axiosInstance.get(
                        `${API_PREFIX}/locations/coordinates.json`,
                        { timeout: COORDINATES_TIMEOUT_MS }
                    );
                    existingCoordinates = Array.isArray(coordinatesResponse.data)
                        ? coordinatesResponse.data
                        : [];
                    console.log('✅ Coordinates loaded successfully:', existingCoordinates.length, 'entries');
                    break;
                } catch (error) {
                    console.error(`❌ Attempt ${attempt + 1} failed:`, error.message);

                    if (attempt === MAX_RETRIES) {
                        console.error('❌ Failed to load coordinates after all retries — continuing without map coordinates');
                    } else {
                        console.log(`⏳ Waiting ${RETRY_DELAY * (attempt + 1)}ms before retry...`);
                        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * (attempt + 1)));
                    }
                }
            }

            const res = await axiosInstance.get(
                `${API_PREFIX}/analytics/listeners-by-region-for-station?${param}`
            );
            const list = [];
            const locationsToFetch = [];
            if (res.data.data[0] && res.data.data[0].length > 0) {
                res.data.data[0].forEach((e) => {
                    if (e.city !== '' && e.city !== undefined && e.city !== null) {
                        const existingCoord = existingCoordinates.find(
                            (coord) =>
                                coord.city === e.city &&
                                coord.country === e.country &&
                                coord.region === e.region
                        );
                        if (existingCoord) {
                            list.push({
                                city: e.city.split(',').at(0) + ', ' + e.region,
                                metrics: e.uniqueListeners,
                                position: {
                                    lat: existingCoord.lat,
                                    lng: existingCoord.lng,
                                },
                            });
                        } else {
                            locationsToFetch.push({
                                city: e.city,
                                country: e.country,
                                region: e.region,
                                uniqueListeners: e.uniqueListeners,
                            });
                        }
                    }
                });
            }

            if (locationsToFetch.length > 0) {
                try {
                    console.log('Fetching missing coordinates for', locationsToFetch.length, 'locations...');
                    const updateResponse = await axiosInstance.post(
                        `${API_PREFIX}/update-coordinates`,
                        { locations: locationsToFetch },
                        { timeout: COORDINATES_TIMEOUT_MS }
                    );

                    if (
                        updateResponse.data.data &&
                        updateResponse.data.data.newCoordinates &&
                        updateResponse.data.data.newCoordinates.length > 0
                    ) {
                        const newCoords = updateResponse.data.data.newCoordinates;
                        newCoords.forEach((coord) => {
                            const matchingLocation = locationsToFetch.find(
                                (loc) =>
                                    loc.city === coord.city &&
                                    loc.country === coord.country &&
                                    loc.region === coord.region
                            );
                            const uniqueListeners =
                                matchingLocation && matchingLocation.uniqueListeners
                                    ? matchingLocation.uniqueListeners
                                    : 0;
                            list.push({
                                city: coord.city.split(',').at(0) + ', ' + coord.region,
                                metrics: uniqueListeners,
                                position: {
                                    lat: coord.lat,
                                    lng: coord.lng,
                                },
                            });
                        });
                        console.log(
                            '✅ Missing coordinates loaded successfully:',
                            newCoords.length,
                            'new coordinates'
                        );
                    } else {
                        console.log('⚠️ No new coordinates returned from update-coordinates API');
                    }
                } catch (error) {
                    console.error('❌ Error fetching missing coordinates:', error.message);
                }
            }

            return list;
        } catch (error) {
            console.error('❌ Error fetching region data:', error.message);
            return [];
        } finally {
            setRegionDataLoading(false);
        }
    };

   

    // Retry function for region data
    const retryRegionData = async () => {
        if (!alias) {
            toast.error("Error occurred while fetching analytics data, Please login again");
            return;
        }
        
        try {
            const param = buildParams();
            if (param) {
                const regionDataResult = await fetchRegionData(param);
                setRegionData(regionDataResult);
                toast.success("Map data refreshed successfully");
            }
        } catch (error) {
            console.error('Error retrying region data:', error);
            setRegionDataLoading(false);
            toast.error("Failed to refresh map data. Please try again.");
        }
    };

    // Fetch iOS and Android app active installs
    const fetchIosStats = async (alias) => {
        const res = await axiosInstance.get(`${API_PREFIX}/stats/ios?alias=${alias}`);
        return res.data.data;
    };
    const fetchAndroidStats = async (alias) => {
        const res = await axiosInstance.get(`${API_PREFIX}/stats/android?alias=${alias}`);
        return res.data.data;
    };

    // Fetch analytics data from API
    const fetchAnalyticsData = async () => {
        setLoading(true);
        setStatsLoading(true);
        const param = buildParams();
        if (!param) {
            setLoading(false);
            setStatsLoading(false);
            setRegionDataLoading(false);
            return;
        }

        // Map/coordinates load independently so a slow/failing coordinates.json
        // does not block charts and stats modules.
        fetchRegionData(param)
            .then((regionDataResult) => setRegionData(regionDataResult))
            .catch((error) => {
                console.error('❌ Region data fetch failed:', error.message);
                setRegionData([]);
                setRegionDataLoading(false);
            });

        const promises = [
            fetchActivityStats(param),
            fetchSessionsStats(param),
            fetchListenersPerDay(param),
            fetchSessionsPerDay(param),
            fetchListeningHoursPerDay(param),
            fetchListeningSessionDuration(param),
            fetchChatMessagesPerDay(param),
            fetchGenderBreakdown(param),
            fetchAgeBreakdown(param),
            fetchIosStats(alias),
            fetchAndroidStats(alias),
            fetchAdsForStation(param),
        ];
        const results = await Promise.allSettled(promises);

        // Helper to get value or fallback
        const get = (idx, fallback) =>
            results[idx].status === 'fulfilled' ? results[idx].value : fallback;

        const activityData = get(0, {});
        setTotalActivity(
            Math.round(
                (activityData.chatMessagesCount || 0) +
                (activityData.pollAnswersCount || 0) +
                (activityData.contestSubmissionCount || 0) +
                (activityData.wheelIconClicksCount || 0)
            )
        );
        setChatMessage(Math.round(activityData.chatMessagesCount || 0));
        setPollsAnswer(Math.round(activityData.pollAnswersCount || 0));
        setContestSubmission(Math.round(activityData.contestSubmissionCount || 0));
        setWheelIconClicks(Math.round(activityData.wheelIconClicksCount || 0));

        const sessionsData = get(1, {});
        setSessionStarts(
            Math.round(
                (sessionsData.sessionLoggedCount || 0) +
                (sessionsData.sessionAnonymousCount || 0)
            )
        );
        setTotalSessions(Math.round(sessionsData.sessionCount || 0));
        setUniqueListeners(
            Math.round(
                (sessionsData.nonRegisteredListeners || 0) +
                (sessionsData.registeredListeners || 0)
            )
        );
        setRegListeners(Math.round(sessionsData.registeredListeners || 0));
        setAverageListeningTime(Math.round(sessionsData.averageListeningTime || 0));

        setListenerData(get(2, []));
        setSessionData(get(3, []));
        setListenerDayData(get(4, []));
        setListeningSessionDuration(get(5, []));
        setChatMessages(get(6, []));
        setGenderBreakdown(get(7, []));
        setAgeBreakdown(get(8, []));

        // iOS and Android stats
        setStats({
            ios: get(9, { weekly: 0, recordsTill: '', all_time: 0 }),
            android: get(10, { weekly: 0, recordsTill: '', all_time: 0 }),
        });
        setAdsForStationData(get(11, []));
        setStatsLoading(false);

        setLoading(false);
    };

    // Handle Apply button
    const handleApply = () => {
        const newFilters = { startDate, endDate, interval, platform, listener };
        setFilters(newFilters);
        fetchAnalyticsData();
    };

    const adAnalytics = useMemo(() => {
        const getMetricType = (name) => {
            const n = String(name || '').toLowerCase();
            if (n.endsWith('_view') || n.includes('view')) return 'view';
            if (n.endsWith('_click') || n.includes('click')) return 'click';
            return null;
        };

        const raw = Array.isArray(adsForStationData) ? adsForStationData : [];
        const rowsByAd = {};
        const datesSet = new Set();
        const adIdsWithViews = new Set();

        raw.forEach((row) => {
            const date = row?.date?.value || row?.date;
            const adIdentifier = row?.ad_identifier;
            const metricName = row?.name;
            const total = Number(row?.total_count || 0);
            if (!date || !adIdentifier || !metricName) return;
            datesSet.add(date);

            if (!rowsByAd[adIdentifier]) rowsByAd[adIdentifier] = { byDay: {}, views: 0, clicks: 0 };
            if (!rowsByAd[adIdentifier].byDay[date]) rowsByAd[adIdentifier].byDay[date] = { views: 0, clicks: 0 };

            const metricType = getMetricType(metricName);
            if (metricType === 'view') {
                rowsByAd[adIdentifier].byDay[date].views += total;
                rowsByAd[adIdentifier].views += total;
                if (total > 0) adIdsWithViews.add(adIdentifier);
            } else if (metricType === 'click') {
                rowsByAd[adIdentifier].byDay[date].clicks += total;
                rowsByAd[adIdentifier].clicks += total;
            }
        });

        const adIdentifiers = Array.from(adIdsWithViews).sort();
        const sortedDates = Array.from(datesSet).sort();
        const toSeries = (kind, selectedAd = '__ALL__') => sortedDates.map((date) => {
            const total = adIdentifiers.reduce((sum, adId) => {
                if (selectedAd !== '__ALL__' && selectedAd !== adId) return sum;
                return sum + (rowsByAd[adId]?.byDay?.[date]?.[kind] || 0);
            }, 0);
            return { date, count: total };
        });

        const tableRows = adIdentifiers.map((adId) => {
            const views = rowsByAd[adId]?.views || 0;
            const clicks = rowsByAd[adId]?.clicks || 0;
            const clickRate = views > 0 ? (clicks / views) * 100 : 0;
            return { adIdentifier: adId, views, clicks, clickRate };
        });

        const grandViews = tableRows.reduce((sum, row) => sum + row.views, 0);
        const grandClicks = tableRows.reduce((sum, row) => sum + row.clicks, 0);
        const grandClickRate = grandViews > 0 ? (grandClicks / grandViews) * 100 : 0;

        return {
            visible: adIdentifiers.length > 0,
            adIdentifiers,
            selectedOptions: [
                { value: '__ALL__', label: 'All Ad Identifiers' },
                ...adIdentifiers.map((id) => ({ value: id, label: id })),
            ],
            viewsByDayAll: toSeries('views'),
            clicksByDayAll: toSeries('clicks'),
            viewsByAd: adIdentifiers.reduce((acc, adId) => {
                acc[adId] = toSeries('views', adId);
                return acc;
            }, {}),
            clicksByAd: adIdentifiers.reduce((acc, adId) => {
                acc[adId] = toSeries('clicks', adId);
                return acc;
            }, {}),
            tableRows,
            grandTotal: { views: grandViews, clicks: grandClicks, clickRate: grandClickRate },
        };
    }, [adsForStationData]);

    const [selectedAdIdentifiers, setSelectedAdIdentifiers] = useState(['__ALL__']);

    useEffect(() => {
        const options = adAnalytics?.selectedOptions || [];
        const validSet = new Set(options.map((opt) => opt.value));
        const validSelected = (Array.isArray(selectedAdIdentifiers) ? selectedAdIdentifiers : ['__ALL__'])
            .filter((v) => validSet.has(v));
        if (!validSelected.length) {
            setSelectedAdIdentifiers(['__ALL__']);
        } else if (validSelected.length !== selectedAdIdentifiers.length) {
            setSelectedAdIdentifiers(validSelected);
        }
    }, [adAnalytics, selectedAdIdentifiers]);

    const buildChartSeries = useCallback((kind) => {
        if (!adAnalytics?.visible) return { data: [], lineDefs: [] };

        const selected = Array.isArray(selectedAdIdentifiers) ? selectedAdIdentifiers : ['__ALL__'];
        const includeTotalOnly = selected.includes('__ALL__');
        if (includeTotalOnly) {
            const data = kind === 'views' ? adAnalytics.viewsByDayAll : adAnalytics.clicksByDayAll;
            const color = kind === 'views' ? '#4A90E2' : '#F37123';
            return { data, lineDefs: [{ key: 'count', label: 'All Ad Identifiers', color }] };
        }

        const selectedIds = selected.filter((v) => v !== '__ALL__');
        const dates = Array.from(new Set(
            selectedIds.flatMap((adId) => ((kind === 'views' ? adAnalytics.viewsByAd : adAnalytics.clicksByAd)?.[adId] || []).map((p) => p.date))
        )).sort();

        const data = dates.map((date) => {
            const row = { date };
            selectedIds.forEach((adId) => {
                const point = ((kind === 'views' ? adAnalytics.viewsByAd : adAnalytics.clicksByAd)?.[adId] || []).find((p) => p.date === date);
                row[adId] = point?.count || 0;
            });
            return row;
        });

        const palette = kind === 'views'
            ? ['#4A90E2', '#39B44A', '#8E2759', '#7ED321', '#6D5BD0', '#00A8A8', '#F37123']
            : ['#F37123', '#4A90E2', '#39B44A', '#8E2759', '#7ED321', '#6D5BD0', '#00A8A8'];
        const lineDefs = selectedIds.map((adId, idx) => ({
            key: adId,
            label: adId,
            color: palette[idx % palette.length],
        }));

        return { data, lineDefs };
    }, [adAnalytics, selectedAdIdentifiers]);

    const activeViewsChart = useMemo(() => buildChartSeries('views'), [buildChartSeries]);
    const activeClicksChart = useMemo(() => buildChartSeries('clicks'), [buildChartSeries]);

    return (
        <div style={styles.PageContainer}>
            <div style={styles.FilterContainer}>
                {/* Interval Select */}
                <select
                    style={styles.Select}
                    value={interval}
                    onChange={e => setInterval(e.target.value)}
                >
                    {intervalItems.map(item => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </select>
                {/* Start Date Picker */}
                <input
                    type="date"
                    style={styles.Date}
                    disabled={interval !== 'custom'}
                    value={startDate.toISOString().split('T')[0]}
                    onChange={e => setStartDate(new Date(e.target.value))}
                />
                {/* End Date Picker */}
                <input
                    type="date"
                    style={styles.Date}
                    disabled={interval !== 'custom'}
                    value={endDate.toISOString().split('T')[0]}
                    min={startDate.toISOString().split('T')[0]}
                    onChange={e => setEndDate(new Date(e.target.value))}
                />
                {/* Platform Select */}
                <select
                    style={styles.Select}
                    value={platform}
                    onChange={e => setPlatform(e.target.value)}
                >
                    {platformItems.map(item => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </select>
                {/* Listener Type Select */}
                <select
                    style={styles.Select}
                    value={listener}
                    onChange={e => setListener(e.target.value)}
                >
                    {listenerItems.map(item => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </select>
                {/* Apply Button */}
                <button style={styles.Button} type="button" onClick={handleApply} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </button>
            </div>
            {/* iOS and Android App Active Installs Cards */}
            <div style={styles.CardRow}>
                <div style={styles.Card}>
                    <div style={styles.CardTitle}>iOS App Active Installs <small>(Records till - {stats.ios.recordsTill ? simpleFormatDate(stats.ios.recordsTill) : '---'})</small></div>
                    {statsLoading ? (
                        <div style={{ textAlign: 'center', fontSize: 18, padding: 20 }}>Loading...</div>
                    ) : (
                        <>
                            <div style={styles.StatRow}>
                                <span style={styles.StatLabel}>Last 7 Days</span>
                                <span style={styles.StatValue}>{stats.ios.weekly}</span>
                            </div>
                            <div style={styles.StatRow}>
                                <span style={styles.StatLabel}>Total</span>
                                <span style={styles.StatValue}>{stats.ios.all_time}</span>
                            </div>
                        </>
                    )}
                </div>
                <div style={styles.Card}>
                    <div style={styles.CardTitle}>Android App Active Installs <small>(Records till - {stats.android.recordsTill || '---'})</small></div>
                    {statsLoading ? (
                        <div style={{ textAlign: 'center', fontSize: 18, padding: 20 }}>Loading...</div>
                    ) : (
                        <>
                            <div style={styles.StatRow}>
                                <span style={styles.StatLabel}>Last 7 Days</span>
                                <span style={styles.StatValue}>{stats.android.weekly}</span>
                            </div>
                            <div style={styles.StatRow}>
                                <span style={styles.StatLabel}>Total</span>
                                <span style={styles.StatValue}>{stats.android.all_time}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
            {/* Main Stats Card */}
            <div style={styles.MainCard}>
                <div style={styles.MainCardInner}>
                    {loading ? (
                        <div style={{ textAlign: 'center', fontSize: 20, padding: 30 }}>Loading...</div>
                    ) : (
                        <div style={styles.FlexRow}>
                            <div>
                                <CountUp style={{ fontSize: 36, fontWeight: 600, color: 'rgb(58, 62, 82)' }} start={0} end={totalActivity + totalSessions || 0} duration={2} />
                                <div style={{ textTransform: 'uppercase', fontSize: 12, color: 'rgb(117, 117, 117)', paddingLeft: 7, display: 'flex', alignItems: 'center' }}>
                                    <svg viewBox="0 0 24 24" style={{ display: 'flex', color: 'rgb(58, 62, 82)', height: 20, width: 20, userSelect: 'none', transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms' }}>
                                        <circle cx="12" cy="12" r="12" fill="#757575" />
                                        <path fill="white" d="M14.9 19.2C14.9 19.2 14.9 19.2 14.9 19.2c-0.3 0-0.6-0.2-0.6-0.5L12.2 8l-2 6.7c-0.1 0.2-0.3 0.4-0.5 0.4 -0.3 0-0.5-0.1-0.6-0.3l-1-2 -1 1C6.9 14 6.8 14 6.6 14 6.4 14 6.3 14 6.1 13.9l-0.9-0.9H3.5c-0.3 0-0.6-0.3-0.6-0.6s0.3-0.6 0.6-0.6h2c0.2 0 0.3 0.1 0.4 0.2l0.6 0.6 1.2-1.2c0.1-0.1 0.3-0.2 0.5-0.2 0.2 0 0.4 0.2 0.5 0.3l0.7 1.4 2.3-7.6c0.1-0.3 0.4-0.5 0.6-0.4 0.3 0 0.5 0.2 0.6 0.5l2.2 11.4 2.2-4.6c0.1-0.2 0.3-0.4 0.6-0.4h2.7c0.3 0 0.6 0.3 0.6 0.6s-0.3 0.6-0.6 0.6h-2.3l-2.8 5.9C15.4 19.1 15.2 19.2 14.9 19.2z" />
                                    </svg>
                                    <span style={{ paddingLeft: 5 }}>TOTAL ACTIVITY</span>
                                </div>
                            </div>
                            <div>
                                <CountUp style={{ fontSize: 36, fontWeight: 600, color: 'rgb(58, 62, 82)' }} start={0} end={totalSessions || 0} duration={2} />
                                <div style={{ textTransform: 'uppercase', fontSize: 12, color: 'rgb(117, 117, 117)', paddingLeft: 7, display: 'flex', alignItems: 'center' }}>
                                    <svg viewBox="0 0 24 24" style={{ display: 'flex', color: 'rgb(58, 62, 82)', height: 20, width: 20, userSelect: 'none', transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms' }}>
                                        <circle cx="12" cy="12" r="12" fill="#4A90E2" />
                                        <path fill="white" d="M12 8.3C9.9 8.3 8.3 10 8.3 12c0 2.1 1.7 3.7 3.7 3.7 2.1 0 3.7-1.7 3.7-3.7C15.7 10 14.1 8.3 12 8.3M16.4 15.6c-0.2-0.2-0.5-0.2-0.8 0 -0.2 0.2-0.2 0.5 0 0.8l1.7 1.7c0.2 0.2 0.5 0.2 0.8 0 0.2-0.2 0.2-0.5 0-0.8L16.4 15.6zM7.6 8.4c0.2 0.2 0.5 0.2 0.8 0 0.2-0.2 0.2-0.5 0-0.8L6.7 5.9c-0.2-0.2-0.5-0.2-0.8 0 -0.2 0.2-0.2 0.5 0 0.8L7.6 8.4zM20.1 11.5h-2.4c-0.3 0-0.5 0.2-0.5 0.5 0 0.3 0.2 0.5 0.5 0.5h2.4c0.3 0 0.5-0.2 0.5-0.5C20.6 11.7 20.4 11.5 20.1 11.5M6.9 12c0-0.3-0.2-0.5-0.5-0.5H3.9c-0.3 0-0.5 0.2-0.5 0.5 0 0.3 0.2 0.5 0.5 0.5h2.4C6.6 12.6 6.9 12.3 6.9 12M16.4 8.4l1.7-1.7c0.2-0.2 0.2-0.5 0-0.8 -0.2-0.2-0.5-0.2-0.8 0l-1.7 1.7c-0.2 0.2-0.2 0.5 0 0.8C15.8 8.6 16.2 8.6 16.4 8.4M7.6 15.6l-1.7 1.7c-0.2 0.2-0.2 0.5 0 0.8 0.2 0.2 0.5 0.2 0.8 0l1.7-1.7c0.2-0.2 0.2-0.5 0-0.8C8.2 15.4 7.8 15.4 7.6 15.6M12 6.9c0.3 0 0.5-0.2 0.5-0.5V4c0-0.3-0.2-0.5-0.5-0.5 -0.3 0-0.5 0.2-0.5 0.5v2.4C11.5 6.7 11.7 6.9 12 6.9M12 17.1c-0.3 0-0.5 0.2-0.5 0.5v2.4c0 0.3 0.2 0.5 0.5 0.5 0.3 0 0.5-0.2 0.5-0.5v-2.4C12.5 17.4 12.3 17.1 12 17.1" />
                                    </svg>
                                    <span style={{ paddingLeft: 5 }}>TOTAL SESSIONS</span>
                                </div>
                            </div>
                            <div>
                                <CountUp style={{ fontSize: 36, fontWeight: 600, color: 'rgb(58, 62, 82)' }} start={0} end={uniqueListeners || 0} duration={2} />
                                <div style={{ textTransform: 'uppercase', fontSize: 12, color: 'rgb(117, 117, 117)', paddingLeft: 7, display: 'flex', alignItems: 'center' }}>
                                    <svg viewBox="0 0 24 24" style={{ display: 'flex', color: 'rgb(58, 62, 82)', height: 20, width: 20, userSelect: 'none', transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms' }}>
                                        <circle cx="12" cy="12" r="12" fill="#39B44A" />
                                        <path fill="white" d="M15.9 8.6c0.8 0 1.4 0.8 1.4 1.8s-0.6 1.8-1.4 1.8c-0.8 0-1.4-0.8-1.4-1.8C14.4 9.4 15.1 8.6 15.9 8.6zM12.1 7.9c1.1 0 2 1.1 2 2.5s-0.9 2.5-2 2.5 -2-1.1-2-2.5C10.2 9 11.1 7.9 12.1 7.9zM8.4 8.6c0.8 0 1.4 0.8 1.4 1.8s-0.6 1.8-1.4 1.8C7.6 12.1 7 11.3 7 10.4 7 9.4 7.6 8.6 8.4 8.6zM18.6 14.4c-0.7 0.5-1.5 0.7-2.4 0.8 -0.1 0.2-0.2 0.5-0.3 0.7 -1.1 0.7-2.3 1.1-3.7 1.1s-2.7-0.4-3.8-1.2c-0.1-0.2-0.2-0.4-0.3-0.7 -0.9-0.1-1.7-0.4-2.4-0.8 -0.2-0.3-0.3-0.6-0.4-0.9 0.1-0.2 0.2-0.5 0.8-0.7 0.8-0.3 1.2-0.5 1.2-0.5 0.3 0.3 0.7 0.5 1.1 0.5 0.4 0 0.8-0.2 1.1-0.5 0 0 0.4 0.2 1.2 0.5 0.6 0.2 0.7 0.5 0.8 0.7l0 0c0.2 0.1 0.4 0.1 0.6 0.1 0.2 0 0.4 0 0.7-0.1l0 0c0.1-0.2 0.2-0.5 0.8-0.7 0.8-0.3 1.2-0.5 1.2-0.5 0.3 0.3 0.7 0.5 1.1 0.5 0.4 0 0.8-0.2 1.1-0.5 0 0 0.4 0.2 1.2 0.5 0.6 0.2 0.7 0.5 0.8 0.7C18.9 13.8 18.7 14.1 18.6 14.4z" />
                                    </svg>
                                    <span style={{ paddingLeft: 5 }}>TOTAL UNIQUE LISTENERS</span>
                                </div>
                            </div>
                            <div>
                                <CountUp style={{ fontSize: 36, fontWeight: 600, color: 'rgb(58, 62, 82)' }} start={0} end={regListeners || 0} duration={2} />
                                <div style={{ textTransform: 'uppercase', fontSize: 12, color: 'rgb(117, 117, 117)', paddingLeft: 7, display: 'flex', alignItems: 'center' }}>
                                    <svg viewBox="0 0 24 24" style={{ display: 'flex', color: 'rgb(58, 62, 82)', height: 20, width: 20, userSelect: 'none', transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms' }}>
                                        <circle cx="12" cy="12" r="12" fill="#F5A623" />
                                        <path fill="white" d="M15.9 8.6c0.8 0 1.4 0.8 1.4 1.8s-0.6 1.8-1.4 1.8c-0.8 0-1.4-0.8-1.4-1.8C14.4 9.4 15.1 8.6 15.9 8.6zM12.1 7.9c1.1 0 2 1.1 2 2.5s-0.9 2.5-2 2.5 -2-1.1-2-2.5C10.2 9 11.1 7.9 12.1 7.9zM8.4 8.6c0.8 0 1.4 0.8 1.4 1.8s-0.6 1.8-1.4 1.8C7.6 12.1 7 11.3 7 10.4 7 9.4 7.6 8.6 8.4 8.6zM18.6 14.4c-0.7 0.5-1.5 0.7-2.4 0.8 -0.1 0.2-0.2 0.5-0.3 0.7 -1.1 0.7-2.3 1.1-3.7 1.1s-2.7-0.4-3.8-1.2c-0.1-0.2-0.2-0.4-0.3-0.7 -0.9-0.1-1.7-0.4-2.4-0.8 -0.2-0.3-0.3-0.6-0.4-0.9 0.1-0.2 0.2-0.5 0.8-0.7 0.8-0.3 1.2-0.5 1.2-0.5 0.3 0.3 0.7 0.5 1.1 0.5 0.4 0 0.8-0.2 1.1-0.5 0 0 0.4 0.2 1.2 0.5 0.6 0.2 0.7 0.5 0.8 0.7l0 0c0.2 0.1 0.4 0.1 0.6 0.1 0.2 0 0.4 0 0.7-0.1l0 0c0.1-0.2 0.2-0.5 0.8-0.7 0.8-0.3 1.2-0.5 1.2-0.5 0.3 0.3 0.7 0.5 1.1 0.5 0.4 0 0.8-0.2 1.1-0.5 0 0 0.4 0.2 1.2 0.5 0.6 0.2 0.7 0.5 0.8 0.7C18.9 13.8 18.7 14.1 18.6 14.4z" />
                                    </svg>
                                    <span style={{ paddingLeft: 5 }}>TOTAL REG. LISTENERS</span>
                                </div>
                            </div>
                            <div>
                                <span style={{ fontSize: 36, fontWeight: 600, color: 'rgb(58, 62, 82)' }}>
                                    {formatSecondsToHHMMSS(averageListeningTime)}
                                </span>
                                <div style={{ textTransform: 'uppercase', fontSize: 12, color: 'rgb(117, 117, 117)', paddingLeft: 7, display: 'flex', alignItems: 'center' }}>
                                    <svg viewBox="0 0 24 24" style={{ display: 'flex', color: 'rgb(58, 62, 82)', height: 20, width: 20, userSelect: 'none', transition: 'all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms' }}>
                                        <circle cx="12" cy="12" r="12" fill="#bf1e2d" />
                                        <path fill="white" d="M14.9 19.2C14.9 19.2 14.9 19.2 14.9 19.2c-0.3 0-0.6-0.2-0.6-0.5L12.2 8l-2 6.7c-0.1 0.2-0.3 0.4-0.5 0.4 -0.3 0-0.5-0.1-0.6-0.3l-1-2 -1 1C6.9 14 6.8 14 6.6 14 6.4 14 6.3 14 6.1 13.9l-0.9-0.9H3.5c-0.3 0-0.6-0.3-0.6-0.6s0.3-0.6 0.6-0.6h2c0.2 0 0.3 0.1 0.4 0.2l0.6 0.6 1.2-1.2c0.1-0.1 0.3-0.2 0.5-0.2 0.2 0 0.4 0.2 0.5 0.3l0.7 1.4 2.3-7.6c0.1-0.3 0.4-0.5 0.6-0.4 0.3 0 0.5 0.2 0.6 0.5l2.2 11.4 2.2-4.6c0.1-0.2 0.3-0.4 0.6-0.4h2.7c0.3 0 0.6 0.3 0.6 0.6s-0.3 0.6-0.6 0.6h-2.3l-2.8 5.9C15.4 19.1 15.2 19.2 14.9 19.2z" />
                                    </svg>
                                    <span style={{ paddingLeft: 5 }}>AVG LISTENING TIME</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Sessions Line Chart Card */}
            <ChartWithDownload
                title="Sessions"
                data={sessionData}
                loading={loading}
                fileName="sessions_data"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sessionData} style={{ outline: 'none' }}>
                        <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                        <XAxis dataKey="date" />
                        <YAxis type="number" />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="sessionCount"
                            stroke="#4A90E2"
                            strokeWidth={2}
                            dot={false}
                            name="Sessions"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ChartWithDownload>
            {/* Logged In Listeners Line Chart Card */}
            <ChartWithDownload
                title="Logged In Listeners"
                data={listenerData}
                loading={loading}
                fileName="logged_in_listeners_data"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={listenerData} style={{ outline: 'none' }}>
                        <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                        <XAxis dataKey="date" />
                        <YAxis type="number" />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="loggedListeners"
                            stroke="#F5A623"
                            strokeWidth={2}
                            dot={false}
                            name="Logged In Listeners"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ChartWithDownload>
            {/* Total Listeners Line Chart Card */}
            <ChartWithDownload
                title="Total Listeners"
                subtitle="(Logged In plus Anonymous)"
                data={listenerData}
                loading={loading}
                fileName="total_listeners_data"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={listenerData} style={{ outline: 'none' }}>
                        <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                        <XAxis dataKey="date" />
                        <YAxis type="number" />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="uniqueListeners"
                            stroke="#39B44A"
                            strokeWidth={2}
                            dot={false}
                            name="Total Listeners"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ChartWithDownload>
            {/* Listening Hours per Day Line Chart Card */}
            <ChartWithDownload
                title="Listening Hours per Day"
                data={listenerDayData}
                loading={loading}
                fileName="listening_hours_per_day_data"
            >
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={listenerDayData} style={{ outline: 'none' }}>
                        <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                        <XAxis dataKey="date" />
                        <YAxis type="number" />
                        <Tooltip />
                        <Line
                            type="monotone"
                            dataKey="listeningHoursPerDay"
                            stroke="#3a3e52"
                            strokeWidth={2}
                            dot={false}
                            name="Listening Hours"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </ChartWithDownload>
            {/* Listening Sessions Duration Distribution & Age Breakdown (side by side) */}
            <div style={styles.FlexGap}>
                {/* Listening Sessions Duration Distribution */}
                <ChartWithDownload
                    title="Listening Sessions Duration Distribution"
                    subtitle="Minutes"
                    data={listeningSessionDuration}
                    loading={loading}
                    fileName="listening_sessions_duration_data"
                    chartCardStyle={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        padding: 20,
                        marginBottom: 20,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        flex: 1,
                        marginRight: 10
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={listeningSessionDuration}>
                            <XAxis type="category" dataKey="name" axisLine={false} tickLine={false} interval={0} />
                            <YAxis type="number" dataKey="Total" />
                            <Tooltip />
                            <Bar dataKey="Total" barSize={100} fill="#7ED321" minPointSize={10} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartWithDownload>
                {/* Chat Messages Line Chart */}
                <ChartWithDownload
                    title="Chat Messages"
                    subtitle="Sent from the App"
                    data={chatMessages}
                    loading={loading}
                    fileName="chat_messages_data"
                    chartCardStyle={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        padding: 20,
                        marginBottom: 20,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        flex: 1,
                        marginLeft: 10
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chatMessages} style={{ outline: 'none' }}>
                            <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                            <XAxis dataKey="date" />
                            <YAxis type="number" />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey="chatMessagesCount"
                                stroke="#F37123"
                                strokeWidth={2}
                                dot={false}
                                name="Chat"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartWithDownload>
                
            </div>
            {/* Gender Breakdown (pie) & Chat Messages (line) */}
            <div style={styles.FlexGap} className="gender-breakdown-chat-messages">
                
                {/* Gender Breakdown Pie */}
                <ChartWithDownload
                    title="Gender Breakdown"
                    subtitle="Logged In Listeners"
                    data={genderBreakdown}
                    loading={loading}
                    fileName="gender_breakdown_data"
                    chartCardStyle={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        padding: 20,
                        marginBottom: 20,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        flex: 1,
                        marginRight: 10,
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <ResponsiveContainer width={250} height={250} style={{margin: 'auto'}}>
                        <PieChart  style={{margin: 'auto'}}>
                            <Pie
                                data={genderBreakdown}
                                innerRadius={30}
                                outerRadius={100}
                                legendType="circle"
                                dataKey="value"
                                nameKey="name"
                            >
                                <Cell fill="#4A90E2" />
                                <Cell fill="#8E2759" />
                                <Cell fill="#F37123" />
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={styles.PieLegend}>
                        <span style={{ ...styles.PieLegendDot, background: '#4A90E2' }}></span> Male
                        <span style={{ ...styles.PieLegendDot, background: '#8E2759', margin: '0 6px 0 16px' }}></span> Female
                        <span style={{ ...styles.PieLegendDot, background: '#F37123', margin: '0 6px 0 16px' }}></span> Other
                    </div>
                </ChartWithDownload>
                
                {/* Age Breakdown */}
                <ChartWithDownload
                    title="Age Breakdown"
                    subtitle="Years - Logged In Listeners"
                    data={ageBreakdown}
                    loading={loading}
                    fileName="age_breakdown_data"
                    chartCardStyle={{
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        padding: 20,
                        marginBottom: 20,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        flex: 1,
                        marginLeft: 10
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={ageBreakdown}>
                            <XAxis type="category" dataKey="name" axisLine={false} tickLine={false} interval={0} />
                            <YAxis type="number" dataKey="Total" />
                            <Tooltip />
                            <Bar dataKey="Total" barSize={100} fill="#4A90E2" minPointSize={10} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartWithDownload>
            </div>
            {adAnalytics?.visible && (
                <div style={styles.AdSectionCard}>
                    <div style={styles.AdSectionHeader}>
                        <h3 style={styles.AdSectionTitle}>AdMax Analytics</h3>
                        <details style={styles.MultiSelect}>
                            <summary style={styles.MultiSelectSummary}>
                                {(selectedAdIdentifiers || []).includes('__ALL__')
                                    ? 'All Ad Identifiers'
                                    : `${(selectedAdIdentifiers || []).length} selected`}
                            </summary>
                            <div style={styles.MultiSelectMenu}>
                                {(adAnalytics.selectedOptions || []).map((opt) => (
                                    <label key={`ad-identifier-${opt.value}`} style={styles.MultiSelectOption}>
                                        <input
                                            type="checkbox"
                                            checked={(selectedAdIdentifiers || ['__ALL__']).includes(opt.value)}
                                            onChange={() => {
                                                setSelectedAdIdentifiers((prev) => {
                                                    const current = Array.isArray(prev) ? prev : ['__ALL__'];
                                                    const has = current.includes(opt.value);
                                                    let next = [...current];

                                                    if (opt.value === '__ALL__') {
                                                        next = ['__ALL__'];
                                                    } else if (has) {
                                                        next = current.filter((v) => v !== opt.value);
                                                    } else {
                                                        next = current.filter((v) => v !== '__ALL__').concat(opt.value);
                                                    }

                                                    if (!next.length) next = ['__ALL__'];
                                                    return next;
                                                });
                                            }}
                                        />
                                        <span>{opt.label}</span>
                                    </label>
                                ))}
                            </div>
                        </details>
                    </div>
                    <div style={styles.AdChartsRow}>
                        <ChartWithDownload
                            title="Ad Views"
                            data={activeViewsChart.data}
                            loading={loading}
                            fileName="ad_views.csv"
                            chartCardStyle={{ marginBottom: 0, border: '1px solid #eef2f8', boxShadow: 'none' }}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={activeViewsChart.data} style={{ outline: 'none' }}>
                                    <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                                    <XAxis dataKey="date" />
                                    <YAxis type="number" />
                                    <Tooltip />
                                    {activeViewsChart.lineDefs.map((line) => (
                                        <Line
                                            key={`views-${line.key}`}
                                            type="monotone"
                                            dataKey={line.key}
                                            stroke={line.color}
                                            strokeWidth={2}
                                            dot={false}
                                            name={line.label}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartWithDownload>
                        <ChartWithDownload
                            title="Ad Clicks"
                            data={activeClicksChart.data}
                            loading={loading}
                            fileName="ad_clicks.csv"
                            chartCardStyle={{ marginBottom: 0, border: '1px solid #eef2f8', boxShadow: 'none' }}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={activeClicksChart.data} style={{ outline: 'none' }}>
                                    <CartesianGrid stroke="#f5f5f5" fill="#fff" />
                                    <XAxis dataKey="date" />
                                    <YAxis type="number" />
                                    <Tooltip />
                                    {activeClicksChart.lineDefs.map((line) => (
                                        <Line
                                            key={`clicks-${line.key}`}
                                            type="monotone"
                                            dataKey={line.key}
                                            stroke={line.color}
                                            strokeWidth={2}
                                            dot={false}
                                            name={line.label}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartWithDownload>
                    </div>
                    <div style={{ marginTop: 4 }}>
                        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10, color: '#2f3442' }}>
                            Ad Identifiers
                        </div>
                        <div style={styles.AdTableWrap}>
                            <table style={styles.AdTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.AdTableHeadCell}>Ad Identifier</th>
                                        <th style={styles.AdTableHeadCell}>Views</th>
                                        <th style={styles.AdTableHeadCell}>Clicks</th>
                                        <th style={styles.AdTableHeadCell}>Clickrate</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {adAnalytics.tableRows.map((row) => (
                                        <tr key={`all-${row.adIdentifier}`}>
                                            <td style={styles.AdTableCell}>{row.adIdentifier}</td>
                                            <td style={styles.AdTableCell}>{row.views}</td>
                                            <td style={styles.AdTableCell}>{row.clicks}</td>
                                            <td style={styles.AdTableCell}>{row.clickRate.toFixed(2)}%</td>
                                        </tr>
                                    ))}
                                    <tr style={styles.AdTableTotalRow}>
                                        <td style={{ ...styles.AdTableCell, borderBottom: 'none' }}>Grand Total</td>
                                        <td style={{ ...styles.AdTableCell, borderBottom: 'none' }}>{adAnalytics.grandTotal.views}</td>
                                        <td style={{ ...styles.AdTableCell, borderBottom: 'none' }}>{adAnalytics.grandTotal.clicks}</td>
                                        <td style={{ ...styles.AdTableCell, borderBottom: 'none' }}>{adAnalytics.grandTotal.clickRate.toFixed(2)}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {/* Map Card: Listeners by Region */}
            <div style={styles.MapSectionWrap}>
                <MapSection 
                    data={regionData} 
                    onRetry={retryRegionData}
                    isLoading={regionDataLoading}
                />
            </div>
        </div>
    );
};

export default AnalyticsManager; 